import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, History } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardStats, RecentMigrations } from '@/components/dashboard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { migrationsApi, migrationKeys } from '@/lib/api';
import { useAuth } from '@/hooks';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Fetch recent migrations for stats (only when authenticated)
  const {
    data: migrationsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: migrationKeys.list({ pageNumber: 1, pageSize: 100 }),
    queryFn: () => migrationsApi.list({ pageNumber: 1, pageSize: 100 }),
    enabled: isAuthenticated,
  });

  return (
    <AppLayout>
      <div className="container max-w-7xl py-8">
        <PageHeader
          title="Dashboard"
        />

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-2 mb-8">
          <Card
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate({ to: '/migrations/new' })}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Start New Migration
              </CardTitle>
              <CardDescription>
                Begin migrating an organization to Amplify
              </CardDescription>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate({ to: '/migrations' })}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-muted-foreground" />
                Migration History
              </CardTitle>
              <CardDescription>
                View and manage past migrations
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Stats and Recent Migrations */}
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <LoadingSpinner size="lg" label="Loading dashboard..." />
          </div>
        ) : isError ? (
          <ErrorMessage
            title="Failed to Load Dashboard"
            error={error}
            onRetry={() => refetch()}
          />
        ) : (
          <>
            {/* Statistics */}
            <DashboardStats
              migrations={migrationsData?.items ?? []}
              className="mb-8"
            />

            {/* Recent Migrations */}
            <RecentMigrations migrations={migrationsData?.items ?? []} />
          </>
        )}
      </div>
    </AppLayout>
  );
}
